import Image from 'next/image';
import { Suspense } from 'react';
import { Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { CarouselControls, ZoomControls } from '@/entities/image-carousel';
import { blurDataURL } from '@/shared/lib';
import { useImageErrors, useImageSwiperNavigation } from '@/shared/lib/hooks';
import { ImageViewWithCarouselSkeleton } from '@/shared/ui/skeletons';

import VerticalThumbnails from './VerticalThumbnails';

import 'swiper/css';

interface Props {
  toggleFullSize: () => void;
  currentImageIndex: number;
  handleImageCurrentIndex: (idx: number) => void;
  pictures: string[];
}

export default function ImageGallery({
  pictures,
  currentImageIndex,
  handleImageCurrentIndex,
  toggleFullSize
}: Props) {
  const { imageErrors, handleImageError } = useImageErrors(pictures);

  const { swiperRef, handlePrev, handleNext, slideTo } = useImageSwiperNavigation();

  return (
    <section className="grid h-[470px] w-full grid-cols-11 gap-6">
      <ZoomControls toggleFullSize={toggleFullSize} />
      <div className="scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-thin col-span-3 overflow-y-auto">
        <VerticalThumbnails
          selectedImage={currentImageIndex}
          images={pictures}
          imageErrors={imageErrors}
          setSelectedImage={slideTo}
          onImageError={handleImageError}
        />
      </div>
      <div className="relative col-span-8">
        <Suspense fallback={<ImageViewWithCarouselSkeleton />}>
          <Swiper
            ref={swiperRef}
            modules={[Virtual]}
            onSlideChange={(swiper) => handleImageCurrentIndex(swiper.activeIndex)}
            initialSlide={currentImageIndex}
          >
            {pictures.map((picture, index) => (
              <SwiperSlide key={index}>
                <Image
                  src={imageErrors[index] ? '/broken-image.svg' : picture}
                  width={640}
                  height={426}
                  alt={`carousel-image-desktop-${index}`}
                  priority={index === 0}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  placeholder="blur"
                  blurDataURL={blurDataURL}
                  unoptimized={true}
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  sizes="(max-width: 768px) 41vw, 60vw"
                  className="h-[520px] w-[470px] rounded-2xl bg-slate-300 object-cover"
                  onError={() => handleImageError(index)}
                  onClick={toggleFullSize}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Suspense>
        <CarouselControls goToNextImage={handleNext} goToPreviousImage={handlePrev} />
      </div>
    </section>
  );
}
